import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { twMerge } from 'tailwind-merge';
import { useDebounced } from '../../hooks/useDebounced';

export interface FilterOption {
  id: number;
  label: string;
  sublabel?: string;
  badge?: string;
}

export interface FilterOptionPage {
  options: FilterOption[];
  total: number;
}

// One page of options per request. The list never loads the whole table.
export const FILTER_PAGE_SIZE = 50;
// Mirrors the server cap on api_key_ids.
export const MAX_FILTER_SELECTION = 100;

// Reserved id for the "all" row. Real ids are always positive.
const ALL_ID = 0;

// Selected ids as the CSV the API expects. Empty selection sends no filter.
export function idsParam(ids: number[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

// Height and font size live on the wrapper so a filter bar can override them.
// The input and an invisible copy of the "all" label share one grid cell, so
// the control is as wide as that label, like a native select. Longer
// selections truncate.
const triggerClass =
  'col-start-1 row-start-1 h-full w-full min-w-0 bg-body-bg border border-gray-600 text-body-color placeholder:text-body-color ' +
  'focus:border-primary focus:outline-none rounded-md pl-3 pr-8 py-1.5';
const sizerClass = 'col-start-1 row-start-1 invisible whitespace-nowrap border border-transparent pl-3 pr-8';

interface EntityFilterProps {
  ariaLabel: string;
  /** Shown when nothing is selected, and as the first row of the list. */
  allLabel: string;
  /** Used for the "3 API keys" summary. */
  pluralNoun: string;
  /** Empty means "all". */
  value: number[];
  onChange: (ids: number[]) => void;
  loadPage: (search: string, offset: number) => Promise<FilterOptionPage>;
  loadSelected?: (ids: number[]) => Promise<FilterOption[]>;
  /** Change this to drop the cached options, e.g. when the parent scope changes. */
  resetKey?: string;
  multiple?: boolean;
  className?: string;
}

function EntityFilter({
  ariaLabel,
  allLabel,
  pluralNoun,
  value,
  onChange,
  loadPage,
  loadSelected,
  resetKey = '',
  multiple = false,
  className = '',
}: EntityFilterProps) {
  const [query, setQuery] = useState('');
  const search = useDebounced(query, 250);
  const [items, setItems] = useState<FilterOption[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [known, setKnown] = useState<Record<number, FilterOption>>({});
  const knownRef = useRef<Record<number, FilterOption>>({});
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  // Drops responses of superseded requests.
  const generation = useRef(0);
  const selectedGeneration = useRef(0);

  const remember = useCallback((options: FilterOption[]) => {
    const remembered = { ...knownRef.current };
    for (const option of options) remembered[option.id] = option;
    knownRef.current = remembered;
    setKnown((prev) => {
      const next = { ...prev };
      for (const option of options) next[option.id] = option;
      return next;
    });
  }, []);

  const fetchPage = useCallback(
    async (offset: number) => {
      const gen = ++generation.current;
      setLoading(true);
      try {
        const page = await loadPage(search, offset);
        if (gen !== generation.current) return;
        setItems((prev) => (offset === 0 ? page.options : [...prev, ...page.options]));
        setTotal(page.total);
        setFailed(false);
        remember(page.options);
      } catch {
        if (gen !== generation.current) return;
        if (offset === 0) {
          setItems([]);
          setTotal(0);
        }
        setFailed(true);
      } finally {
        if (gen === generation.current) setLoading(false);
      }
    },
    [loadPage, search, remember],
  );

  const lastReset = useRef(resetKey);

  // A new search or a new scope always restarts at the first page. A new scope
  // also drops the cached options, so the old scope's rows never stay on screen.
  useEffect(() => {
    if (lastReset.current !== resetKey) {
      lastReset.current = resetKey;
      setItems([]);
      setTotal(0);
      knownRef.current = {};
      setKnown({});
      setFailed(false);
    }
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, resetKey]);

  const valueKey = value.join(',');

  useEffect(() => {
    const gen = ++selectedGeneration.current;
    if (!loadSelected) return;

    const selectedIds = [...valueRef.current];
    const missing = selectedIds.filter((id) => !knownRef.current[id]);
    if (missing.length === 0) return;

    void loadSelected(missing)
      .then((options) => {
        if (gen !== selectedGeneration.current) return;

        const returned = new Set(options.map((option) => option.id));
        const validValue = selectedIds.filter((id) => returned.has(id) || knownRef.current[id]);
        remember(options);
        if (validValue.length !== selectedIds.length) onChangeRef.current(validValue);
      })
      .catch(() => {
        // Keep unresolved ids active when the request fails.
      });

    return () => {
      if (gen === selectedGeneration.current) selectedGeneration.current += 1;
    };
  }, [loadSelected, remember, resetKey, valueKey]);

  const allOption = useMemo<FilterOption>(() => ({ id: ALL_ID, label: allLabel }), [allLabel]);

  const selected = useMemo<FilterOption[]>(() => {
    if (value.length === 0) return [allOption];
    return value.map((id) => known[id] ?? { id, label: `#${id}` });
  }, [value, known, allOption]);

  const summary = useMemo(() => {
    if (value.length === 0) return allLabel;
    if (value.length === 1) return selected[0].label;
    return `${value.length} ${pluralNoun}`;
  }, [value, selected, allLabel, pluralNoun]);

  const commit = (options: FilterOption[]) => {
    const ids = options.filter((option) => option.id !== ALL_ID).map((option) => option.id);
    // Picking "all" while something is selected clears the selection.
    const pickedAll = options.some((option) => option.id === ALL_ID) && value.length > 0;
    onChange(pickedAll ? [] : ids.slice(0, MAX_FILTER_SELECTION));
  };

  const atCap = value.length >= MAX_FILTER_SELECTION;
  const hasMore = items.length < total;
  const rows = [allOption, ...items];

  const panel = (
    <ComboboxOptions
      // Inline, not portalled: the filter bars have no clipping ancestor.
      // top-full: in a grid wrapper the static position would be the top.
      className="absolute left-0 top-full z-30 mt-1 w-[320px] max-w-[calc(100vw-2rem)] rounded-md border border-gray-600 bg-body-bg shadow-lg focus:outline-none"
    >
      <div className="max-h-[320px] overflow-y-auto py-1">
        {rows.map((option) => (
          <ComboboxOption
            key={option.id}
            value={option}
            className="group flex cursor-pointer items-start gap-2 px-3 py-2 text-sm data-[focus]:bg-widget-bg"
          >
            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 invisible text-primary group-data-[selected]:visible" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body-color">{option.label}</span>
              {option.sublabel && (
                <span className="block truncate text-xs text-secondary">{option.sublabel}</span>
              )}
            </span>
            {option.badge && (
              <span className="shrink-0 rounded bg-red-600/20 px-1.5 py-0.5 text-xs text-red-400">
                {option.badge}
              </span>
            )}
          </ComboboxOption>
        ))}
        {loading && <div className="px-3 py-2 text-sm text-secondary">Loading…</div>}
        {failed && <div className="px-3 py-2 text-sm text-red-400">Failed to load options.</div>}
        {!loading && !failed && items.length === 0 && (
          <div className="px-3 py-2 text-sm text-secondary">No matches.</div>
        )}
        {hasMore && (
          <button
            type="button"
            disabled={loading}
            onClick={() => fetchPage(items.length)}
            className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-widget-bg disabled:text-secondary"
          >
            Load more ({items.length} of {total})
          </button>
        )}
      </div>
      {multiple && (
        <div className="flex items-center justify-between gap-2 border-t border-gray-700 px-3 py-2 text-xs text-secondary">
          <span>{atCap ? `Limit of ${MAX_FILTER_SELECTION} reached` : summary}</span>
          <button
            type="button"
            disabled={value.length === 0}
            onClick={() => onChange([])}
            className="text-primary disabled:cursor-default disabled:text-secondary"
          >
            Clear
          </button>
        </div>
      )}
    </ComboboxOptions>
  );

  const trigger = (
    <>
      <span aria-hidden="true" className={sizerClass}>{allLabel}</span>
      <ComboboxInput
        aria-label={ariaLabel}
        className={triggerClass}
        size={1}
        autoComplete="off"
        value={query}
        placeholder={summary}
        title={summary}
        onChange={(event) => setQuery(event.target.value)}
      />
      {/* Chevron placed like the arrow of a native select. */}
      <ComboboxButton
        aria-label={`Open ${ariaLabel}`}
        className="absolute inset-y-0 right-0 flex items-center pl-2"
      >
        <ChevronDownIcon className="h-5 w-5 text-secondary" />
      </ComboboxButton>
    </>
  );

  return (
    <div className={twMerge('relative inline-grid h-[34px] text-sm', className)}>
      {multiple ? (
        <Combobox
          multiple
          immediate
          by="id"
          value={selected}
          onChange={commit}
          onClose={() => setQuery('')}
        >
          {trigger}
          {panel}
        </Combobox>
      ) : (
        <Combobox
          immediate
          by="id"
          value={selected[0]}
          onChange={(option: FilterOption | null) => commit(option ? [option] : [])}
          onClose={() => setQuery('')}
        >
          {trigger}
          {panel}
        </Combobox>
      )}
    </div>
  );
}

export default EntityFilter;
